import React from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useUserInfo } from "@/hooks/useUserInfo";
import { useArabicName } from "@/hooks/useArabicName";
import { getGreeting } from "@/utils/userHelpers";
import { Skeleton } from "@/components/ui/skeleton";

// Greeting block shown at the top of Home, Attendance and Tasks.
export const WelcomeHeader: React.FC = () => {
  const { t, language } = useLanguage();
  const { greeting: defaultGreeting, fullName, email, role, profilePictureUrl } = useUserInfo();
  const isArabic = language === "ar";
  const { name: arabicName, loading: arabicNameLoading } = useArabicName(email, isArabic);

  // In Arabic, address the employee by their Arabic name - never flash the
  // Microsoft 365 (English) name first. Fall back to it only if there's no Arabic name.
  const waitingForName = isArabic && !arabicName && (arabicNameLoading || !email);
  const greeting =
    isArabic && arabicName ? getGreeting(arabicName, language) : defaultGreeting;
  const greetingWord = getGreeting("", language).replace("👋", "").trim();

  return (
    <section className="flex items-center gap-4 sm:gap-6 pt-2">
      <div className="h-16 w-16 sm:h-20 sm:w-20 shrink-0 overflow-hidden rounded-full bg-ink text-white ring-4 ring-card shadow-[var(--shadow-md)] flex items-center justify-center">
        {profilePictureUrl ? (
          <img src={profilePictureUrl} alt={fullName} className="h-full w-full object-cover" />
        ) : (
          <span className="text-2xl sm:text-3xl font-bold">
            {fullName ? fullName.charAt(0).toUpperCase() : "U"}
          </span>
        )}
      </div>
      <div className="min-w-0">
        {role && email && (
          <p className="mb-1 text-xs sm:text-sm font-medium text-primary break-words">
            {role} - {email}
          </p>
        )}
        <h1 className="text-2xl sm:text-4xl lg:text-5xl font-extrabold leading-tight">
          {waitingForName ? (
            <>
              {greetingWord}{" "}
              <Skeleton className="inline-block h-[0.8em] w-[6em] align-middle rounded-lg" />
            </>
          ) : (
            greeting
          )}
        </h1>
        <p className="mt-1 text-sm sm:text-lg text-muted-foreground">
          {t("dashboard.activity_overview")}
        </p>
      </div>
    </section>
  );
};
